import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { ComponentSizes, OptionKeyValue, OptionSelectComponent, SurveyAccount } from '@myriadgenetics/mgh-types';
import { Text2 } from '../../elements/typography';
import { OptionSelect } from '../question';
import { useHistory } from '../../helpers/routing';
import aux from '../../helpers/auxiliary-page';
import QuestionText from '../../elements/question-text';

import './index.scss';
import Button from '../../elements/button';
import { useLocation } from 'react-router-dom';
import { LocationState } from '../survey-container/types';
import ProviderHook from './hooks/useSetProvider';
import { InputTextArea } from '../rac/textarea';
import { useButtonsWrapperContext } from '../buttons-wrapper';
import { BackButton } from '../buttons/back';

type Provider = string | number | null | undefined;
type Clinic = string;

interface Props {
  surveyAccounts: SurveyAccount[];
}

interface ClinicAccount {
  name: string | number;
  surveyAccountId: number | string;
}

interface ClinicMap {
  [clinicName: string]: ClinicAccount[];
}

export const getClinicName = (surveyAccount: SurveyAccount): string => surveyAccount.clinicName || 'N/A';

const getProviderName = (surveyAccount: SurveyAccount): string | number => surveyAccount.providerName || surveyAccount.surveyId;

const getSurveyAccountById = (surveyAccountId: number | undefined, surveyAccounts: SurveyAccount[]): SurveyAccount | null | undefined =>
  surveyAccounts.find((sa) => sa.id === surveyAccountId);

const getSurveyAccountByProvider = (providerList: OptionKeyValue[], provider: Provider): Provider[] =>
  providerList.filter((p) => p.optionValue === provider).map((p) => p.optionKey);

const parseSurveyAccounts = (surveyAccounts: SurveyAccount[]): ClinicMap => {
  const clinicMap: ClinicMap = {};
  surveyAccounts.forEach((sa) => {
    const clinicName = getClinicName(sa);
    if (!clinicMap[clinicName]) {
      clinicMap[clinicName] = [];
    }

    clinicMap[clinicName].push({
      name: getProviderName(sa),
      surveyAccountId: sa.id,
    });
  });

  // sort by provider name
  Object.keys(clinicMap).forEach((c) => {
    clinicMap[c] = clinicMap[c].sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
  });
  return clinicMap;
};

function ProviderSelect({ surveyAccounts }: Props) {
  const { buttonsRef } = useButtonsWrapperContext();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation() as LocationState;
  const onProviderSelected = ProviderHook.useSetProvider();
  const clinicMap = parseSurveyAccounts(surveyAccounts);
  const clinicNames = Object.keys(clinicMap);
  const sortedClinicNames = clinicNames.sort((a, b) => a.localeCompare(b));
  const mghOrderState = location?.state?.locResponses?.mghOrderData || null;
  const { clinicName: mghOrderClinic = null } = mghOrderState || {};
  const mghOrderClinicOrSorted = mghOrderClinic || sortedClinicNames[0];
  const isSingleClinic = mghOrderClinic || clinicNames.length === 1;
  const clinic: Clinic = isSingleClinic ? mghOrderClinicOrSorted : location.state?.clinicName || '';

  const providers: OptionKeyValue[] = useMemo(
    () =>
      clinic
        ? clinicMap[clinic].map((p) => ({
            optionKey: p.surveyAccountId,
            optionValue: `${p.name}`,
          }))
        : [],
    [clinic, clinicMap],
  );

  const { providerName: mghOrderProvider = null } = mghOrderState || {};
  const isSingleProvider = mghOrderProvider || providers.length === 1;
  const isSelectedNeeded = !(isSingleClinic && isSingleProvider);
  const provider: Provider = isSingleProvider ? mghOrderProvider || providers[0].optionValue : location.state?.surveyAccount?.providerName || '';

  const surveyAccount: SurveyAccount | undefined = isSingleProvider
    ? getSurveyAccountById(Number.parseInt(providers[0].optionKey.toString(), 10), surveyAccounts) ?? undefined
    : location.state?.surveyAccount;

  const handleProviderSelected = (surveyAccount: SurveyAccount | null | undefined) => {
    if (surveyAccount) {
      onProviderSelected(surveyAccount, getClinicName(surveyAccount), true, false);
    } else {
      aux.routeToAuxPage(history, {
        titleKey: 'errorPages.server.title',
        messageKey: 'errorPages.server.message',
      });
    }
  };

  const clinicsComponent: OptionSelectComponent = {
    id: 1,
    type: 'OPTION_SELECT',
    data: {
      answerKey: 'providerSelectClinic',
      isNative: true,
      isSingleSelect: true,
      labelKey: 'provider.clinicName',
      optionSize: ComponentSizes.Large,
      placeholderKey: clinicNames.length > 0 ? 'provider.selectClinic' : ' ',
      questionKey: '',
      required: true,
      selectionOptions: clinicNames.map((name) => ({
        optionKey: name,
        optionValue: name,
      })),
      subtitleKey: '',
    },
  };

  const providersComponent: OptionSelectComponent = {
    id: 2,
    type: 'OPTION_SELECT',
    data: {
      answerKey: 'providerSelectProvider',
      isNative: true,
      isSingleSelect: true,
      labelKey: 'provider.name',
      optionSize: ComponentSizes.Large,
      placeholderKey: providers.length > 0 ? 'provider.selectProvider' : ' ', // space so this seems incomplete till clinic is selected
      questionKey: '',
      required: true,
      selectionOptions: providers,
      subtitleKey: '',
    },
  };

  return (
    <div className="provider-select">
      <div className="provider-select__question">
        <QuestionText htmlFor="optionSelect" questionKey={!isSelectedNeeded ? 'provider.delivered' : 'provider.question'} />
      </div>
      {clinic !== 'N/A' && isSingleClinic && (
        <div className="provider-select__clinic-container">
          <InputTextArea label={t('provider.clinicName')} value={clinic} isReadOnly />
        </div>
      )}
      {clinic !== 'N/A' && !isSingleClinic && (
        <OptionSelect
          className="provider-select__clinic-option-select"
          compDef={clinicsComponent}
          responses={{
            providerSelectClinic: clinic ? [{ optionKey: clinic, optionValue: clinic }] : undefined,
          }}
          onResponseUpdated={(key: string, val: OptionKeyValue[]) => {
            onProviderSelected(undefined, val[0].optionValue, false, true);
          }}
        />
      )}
      {provider && isSingleProvider && (
        <div className="provider-select__provider-container">
          <InputTextArea label={t('provider.name')} value={provider} isReadOnly />
        </div>
      )}
      {clinic && !isSingleProvider && (
        <OptionSelect
          className="provider-select__provider-option-select"
          compDef={providersComponent}
          icon={faUser}
          responses={{
            providerSelectProvider: getSurveyAccountByProvider(providers, provider) || undefined,
          }}
          onResponseUpdated={(key, val) => {
            const surveyAccount = getSurveyAccountById(val[0].optionKey, surveyAccounts);
            if (surveyAccount) {
              onProviderSelected(surveyAccount, clinic, false, true);
            }
          }}
          key={clinic}
        />
      )}
      {isSelectedNeeded && <Text2 className="provider-select__help">{t('provider.help')}</Text2>}
      {buttonsRef?.current ? (
        createPortal(
          <>
            <BackButton />
            <Button
              disabled={!provider || !clinic}
              className="provider-select__continue-button"
              dataTestId="continue-button"
              onClick={() => handleProviderSelected(surveyAccount)}
              size="small"
            >
              {t('continue')}
            </Button>
            <BackButton />
          </>,
          buttonsRef.current,
        )
      ) : (
        <Button
          disabled={!provider || !clinic}
          className="provider-select__continue-button"
          dataTestId="continue-button"
          onClick={() => handleProviderSelected(surveyAccount)}
          size="small"
        >
          {t('continue')}
        </Button>
      )}
    </div>
  );
}

export default ProviderSelect;
